/**
 * Network helpers — withTimeout, isOffline, NetworkError.
 *
 * Used to wrap Amplify auth calls and API calls so the app never hangs
 * on a stuck Cognito or fetch request when the device is offline.
 *
 * Canonical pattern: shared-docs/offline-first-architecture.md (rule 1).
 */

export class NetworkError extends Error {
  constructor(message, { cause, code } = {}) {
    super(message);
    this.name = 'NetworkError';
    if (cause) this.cause = cause;
    if (code) this.code = code;
  }
}

/**
 * Race a promise against a timeout. Resolves with the promise's value or
 * rejects with NetworkError('timeout').
 *
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @param {string} [label]
 * @returns {Promise<T>}
 */
export function withTimeout(promise, ms, label = 'operation') {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(
      () => reject(new NetworkError(`${label} timed out after ${ms}ms`, { code: 'TIMEOUT' })),
      ms,
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Best-effort offline check. WKWebView reports navigator.onLine reliably
 * once mounted, but it can be flaky on first launch — callers that gate
 * sync logic should re-check after a short delay.
 *
 * @returns {boolean}
 */
export function isOffline() {
  if (typeof navigator === 'undefined') return false;
  if (!('onLine' in navigator)) return false;
  return navigator.onLine === false;
}

/** Default auth-call timeout (Cognito hangs offline). */
export const AUTH_TIMEOUT_MS = 4_000;
/** Default pull timeout for the first dashboard / sessions fetch. */
export const PULL_TIMEOUT_MS = 8_000;
